/**
 * The fan's staged and saved picks for the round, in priority order, each
 * with its game state, outcome tone, a Remove button and, in a multi pick
 * round, move buttons around the priority number (legacy
 * `selected_pick_tile.dart`) (S3, S4, S6, S7).
 */
import { Button, cn } from "@sportsmet/ui";
import type { JSX } from "react";

import { TeamMark } from "../gameDetail/TeamMark";
import { PICK_TONE_CLASS, type typeMyPickCard } from "./survivorCardModel";
import {
  SURVIVOR_MY_PICKS_HEADING,
  survivorMoveLabel,
  survivorRemoveLabel,
  survivorVersusLabel,
} from "./survivorGameCopy";

export type typeMyPicksListProps = {
  cards: readonly typeMyPickCard[];
  showPriority: boolean; // requiredPickCount > 1
  disabled: boolean; // phase is not "open"
  onMove: (input: { eventId: string; priority: number }) => void;
  onRemove: (eventId: string) => void;
};

function TrashIcon(): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        d="M4 6h12M8 6V4.5A1.5 1.5 0 0 1 9.5 3h1A1.5 1.5 0 0 1 12 4.5V6m2 0-.6 9.6a1.5 1.5 0 0 1-1.5 1.4H8.1a1.5 1.5 0 0 1-1.5-1.4L6 6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronLeftIcon(): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="m12 4-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightIcon(): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="m8 4 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function MyPicksList(props: typeMyPicksListProps): JSX.Element | null {
  if (props.cards.length === 0) {
    return null;
  }

  return (
    <section data-testid="survivor-my-picks" className="flex flex-col gap-2">
      <h2 className="text-body-strong text-ink">{SURVIVOR_MY_PICKS_HEADING}</h2>
      <div className="flex gap-3 overflow-x-auto">
        {props.cards.map((card) => (
          <div
            key={card.eventId}
            data-testid={`survivor-my-pick-${card.eventId}`}
            data-tone={card.tone}
            className={cn(
              "flex w-48 shrink-0 flex-col items-center gap-2 rounded-md p-3 text-center",
              PICK_TONE_CLASS[card.tone],
            )}
          >
            {card.basisLabel ? (
              <span className="text-caption text-ink-secondary">
                {card.basisLabel}
              </span>
            ) : null}
            <div className="flex w-full items-center justify-between gap-2">
              <span className="text-caption text-ink-secondary">
                {card.gameStateLabel}
              </span>
              {!props.disabled && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={survivorRemoveLabel(card.teamName)}
                  data-testid={`survivor-remove-${card.eventId}`}
                  onClick={() => {
                    props.onRemove(card.eventId);
                  }}
                >
                  <TrashIcon />
                </Button>
              )}
            </div>
            <TeamMark
              name={card.teamName}
              abbreviation={card.abbreviation}
              logoUrl={card.logoUrl}
              size="md"
            />
            <span className="text-body-strong text-ink">{card.teamName}</span>
            <span className="text-caption text-ink-secondary">
              {survivorVersusLabel(card.opponentName)}
            </span>
            {card.toneLabel !== null && (
              <span className="text-caption font-semibold text-ink">
                {card.toneLabel}
              </span>
            )}
            {props.showPriority && (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={survivorMoveLabel(
                    card.teamName,
                    card.priority - 1,
                  )}
                  data-testid={`survivor-move-up-${card.eventId}`}
                  disabled={props.disabled || card.priority === 1}
                  onClick={() => {
                    props.onMove({
                      eventId: card.eventId,
                      priority: card.priority - 1,
                    });
                  }}
                >
                  <ChevronLeftIcon />
                </Button>
                <span
                  data-testid={`survivor-my-pick-priority-${card.eventId}`}
                  className="min-w-11 rounded-md border border-hairline px-2 py-1 text-body-strong tabular-nums"
                >
                  {card.priority}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={survivorMoveLabel(
                    card.teamName,
                    card.priority + 1,
                  )}
                  data-testid={`survivor-move-down-${card.eventId}`}
                  disabled={
                    props.disabled || card.priority === props.cards.length
                  }
                  onClick={() => {
                    props.onMove({
                      eventId: card.eventId,
                      priority: card.priority + 1,
                    });
                  }}
                >
                  <ChevronRightIcon />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
